import { CommonModule } from '@angular/common';
import { AfterViewInit, CUSTOM_ELEMENTS_SCHEMA, Component, ElementRef, ViewChild, computed, effect, inject, input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ChatInputService } from '@app/services/chat-input.service';
import { EVENT_SCHEMA, EventTrackerInternalService } from '@app/services/event-tracker-2.service';
import { EventTrackerService, QiraEventHumanReadable } from '@app/services/event-tracker.service';
import { ScrollService } from '@app/services/scroll.service';
import { TabService } from '@app/services/tab.service';
import { DisplayVehicle, SendMessage } from '@app/services/types';
import { VoiceService } from '@app/services/voice.service';
import { TranslocoPipe } from '@jsverse/transloco';
import { Button } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-chat-input',
  templateUrl: './chat-input.component.html',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, FormsModule, Button, ToggleSwitchModule, TooltipModule, TranslocoPipe],
})
export class ChatInputComponent implements AfterViewInit {
  tabId = input<string | undefined>(undefined);

  @ViewChild('chatInput') chatInputElement?: ElementRef;

  message = '';

  tabService = inject(TabService);
  scrollService = inject(ScrollService);
  voiceService = inject(VoiceService);
  eventTracker = inject(EventTrackerService);
  eventTrackerInternal = inject(EventTrackerInternalService);
  chatInputService = inject(ChatInputService);

  observeChatInput = effect(() => {
    const chatInput = this.chatInputService.getChatInput(this.tabId());

    if (chatInput) {
      this.message = chatInput;
    }
    this.chatInputElement?.nativeElement.focus();
  });

  currentThread = computed(() => this.tabService.activeTab());
  hasMessages = computed(() => this.tabService.activeTab()?.messages?.length ?? 0 > 0);

  disabled = computed(() => {
    const isDisabled = !!this.tabService.tabsLoading() || !!this.tabService.activeTab()?.isPolling;

    if (!isDisabled) {
      setTimeout(() => {
        this.chatInputElement?.nativeElement.focus();
      }, 50);
    }

    return isDisabled;
  });

  vehicle = computed<DisplayVehicle | undefined>(() => {
    const tab = this.tabService.activeTab() as any;
    if (!tab) return undefined;

    // Prefer mapped property (ChatTab.vehicle)
    if (tab.vehicle) {
      return tab.vehicle as DisplayVehicle;
    }

    // Fallback to API structure (thread.session.vehicle) - during initial load
    if (tab.session?.vehicle) {
      return tab.session.vehicle as DisplayVehicle;
    }

    return undefined;
  });

  // Always show input if we have a valid tab, even if vehicle isn't resolved yet
  shouldShowInput = computed(() => {
    const tab = this.tabService.activeTab();
    const tabIdValue = this.tabId();
    // Show input as long as we have both tabId and an active tab
    return !!tabIdValue && !!tab;
  });

  ngAfterViewInit() {
    this.chatInputElement?.nativeElement.focus();
  }

  sendMessage(message: SendMessage) {
    this.tabService.submitQuestion(message);
    this.chatInputService.clearSuggestions(message.threadId);
  }

  adjustTextareaHeight($event: any) {
    const textarea = $event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 2 + 'px';
  }

  public onKeyDown($event: KeyboardEvent): void {
    if ($event.code === 'Space') {
      if (this.voiceService.isPlaying()) {
        this.voiceService.stopAudio();
      }
    } else if ($event.key === 'Enter' && !$event.shiftKey) {
      $event.preventDefault();
      this.sendChatMessage('return');
    } else if ($event.key === 'Enter' && $event.shiftKey) {
      this.adjustTextareaHeight($event);
    }
  }

  public preprocessMessage(message: string) {
    return message.replace(/\n(?!\n)/g, '\n\n');
  }

  public sendChatMessage(triggerType = 'click'): void {
    if (this.message.length > 0) {
      const textarea = this.chatInputElement?.nativeElement as HTMLTextAreaElement;
      textarea.style.height = 'auto';

      const tabId = this.tabId();
      if (tabId) {
        this.sendMessage({
          threadId: tabId,
          text: this.preprocessMessage(this.message),
        });
        this.chatInputService.clearChatInput(tabId);
        this.chatInputService.clearSuggestions(tabId);

        const question = this.message + '';

        this.message = '';

        // immediate scroll to the new message
        this.scrollService.scrollToMessage('tmp', this.tabService.activeTab());

        // deprecated event tracking
        this.eventTracker.logEvent(QiraEventHumanReadable.USER_SEND_QUESTION, {
          thread_id: tabId,
          question_text: question,
          trigger_type: triggerType,
        });

        this.eventTrackerInternal.trackEvent(EVENT_SCHEMA.CORE_PRODUCT.QA.QUESTION_SUBMITTED, {
          thread_id: tabId,
          question_text: question,
          trigger_type: triggerType,
        });
      } else {
        console.error('[ChatInputComponent] Missing tabId when sending message');
      }
    }
  }
}
